import { ArrowForwardIcon } from "@chakra-ui/icons";
import { WarningIcon } from "@chakra-ui/icons";
import { Text } from "@chakra-ui/react";
import { PropsWithChildren, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { FileDescriptorRead, Status } from "@nens/rana/api-v1";
import { HistoryIcon, RanaButton, StorageIcon, TenantContext } from "@nens/ranalib";

import { RanaLink } from "@/components/rana-link";
import { getProjectTabQueryParam } from "@/features/project/project-tabs";
import { FileDataTypeEnum } from "@/lib/rana-file-data-type/enums/file-data-type";
import { ProjectContext } from "@/stores/project-provider";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";

import { useRestartFile } from "../api/restart-file";

import { ProjectFilePreviewProps } from "./project-file-preview";
import { ProjectFileStatus, ProjectFileStatusWithProgress } from "./project-file-status";

const REGENERATEABLE_DATA_TYPES: string[] = [
  FileDataTypeEnum.Raster,
  FileDataTypeEnum.Scenario,
  FileDataTypeEnum.Vector,
];

const CurrentVersionLink = ({ fileId }: { fileId: string }) => {
  const { tenant } = useContext(TenantContext);
  const { slug } = useContext(ProjectContext);
  const intl = useIntl();

  return (
    <RanaLink
      // Also provide a to here for when we are linking to a file from a process
      to={`/${tenant}/projects/${slug}`}
      queryParams={`${getProjectTabQueryParam("files")}&${convertFileIdToQueryParams(fileId)}`}
      title={intl.formatMessage({
        id: "processresults.open_file_preview",
        defaultMessage: "Open in file preview",
      })}
      textDecor="none !important"
      color="brand.analytic"
    >
      Go to latest version of this file
      <ArrowForwardIcon ml={1} />
    </RanaLink>
  );
};

interface ProjectFilePreviewStatusProps extends ProjectFilePreviewProps {
  status?: FileDescriptorRead["status"];
  isError?: boolean;
}

export function ProjectFilePreviewStatus({
  status,
  isError,
  file,
  fileRef,
}: PropsWithChildren<ProjectFilePreviewStatusProps>) {
  const { mutate, isMutating } = useRestartFile();
  const restartFileDescriptor = (fileRef: string) => mutate({ file, ref: fileRef });

  if (isError) {
    return (
      <ProjectFileStatus icon={WarningIcon} color="semantic.error.main">
        <FormattedMessage
          id="filepreview.failed_to_load_descriptor"
          defaultMessage="Failed to load file descriptor"
        />
      </ProjectFileStatus>
    );
  }

  if (
    status?.id === Status.Pending ||
    status?.id === Status.Restarting ||
    status?.id === Status.Retry
  ) {
    return (
      <ProjectFileStatusWithProgress progress={status.progress}>
        <FormattedMessage
          id="filepreview.loading"
          defaultMessage="Preview for this file is loading"
        />
      </ProjectFileStatusWithProgress>
    );
  }

  if (status?.id === Status.Processing) {
    return (
      <ProjectFileStatusWithProgress progress={status.progress}>
        <FormattedMessage
          id="filepreview.processing"
          defaultMessage="Preview for this file is being processed"
        />
      </ProjectFileStatusWithProgress>
    );
  }

  if (status?.id === Status.Failed) {
    return (
      <ProjectFileStatus icon={StorageIcon}>
        {status.messageI18n != null ? (
          <FormattedMessage
            id={status.messageI18n.type}
            defaultMessage={status.messageI18n.msg}
            values={status.messageI18n.ctx || {}}
          />
        ) : (
          <FormattedMessage
            id="filepreview.failed_to_process"
            defaultMessage="Preview for this file is not available"
          />
        )}
      </ProjectFileStatus>
    );
  }

  if (status?.id === Status.Deleted || status?.id === Status.Deleting) {
    return (
      <ProjectFileStatus icon={WarningIcon} color="semantic.error.main">
        <FormattedMessage
          id="filepreview.deleted"
          defaultMessage="This file has been overwritten (or deleted)"
        />
        <Text>Preview for this file is not available</Text>
        {fileRef && <CurrentVersionLink fileId={file.id} />}
        {fileRef && file.dataType && REGENERATEABLE_DATA_TYPES.includes(file.dataType) && (
          <RanaButton
            mt={4}
            variant="secondary"
            leftIcon={<HistoryIcon />}
            onClick={() => restartFileDescriptor(fileRef)}
            isDisabled={isMutating}
          >
            <FormattedMessage
              id="filepreview.restart.descriptor"
              defaultMessage="Regenerate preview"
            />
          </RanaButton>
        )}
      </ProjectFileStatus>
    );
  }

  // No preview available
  return (
    <ProjectFileStatus icon={StorageIcon}>
      <FormattedMessage
        id="projectfile.no_preview"
        defaultMessage="Preview for this file is not available"
      />
    </ProjectFileStatus>
  );
}
